import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo, breadcrumbJsonLd, faqJsonLd, articleJsonLd } from "../lib/seo";
import { Section, SectionHead, Breadcrumb, Button, Badge, Spinner } from "../components/ui";
import { Markdown } from "../components/markdown";
import { FaqAccordion, ConversationTranscript } from "../components/marketing";
import { useReveal } from "../lib/useReveal";

export function UseCasesIndex() {
  const { data } = useQuery({ queryKey: ["use-cases"], queryFn: () => get("/content", { type: "use-case" }) });
  useReveal(data);
  return (
    <>
      <Seo title="Use cases" path="/use-cases/" description="Who uses BloomCONNECT to close deals in DMs. Pick your vertical, see the maths — from high-ticket services to ecommerce and real estate." />
      <Section>
        <SectionHead label="Use cases" title="Who uses BloomCONNECT to close deals in DMs." intro="Pick your vertical, see the maths." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {(data || []).map((p) => (
            <Link key={p.slug} to={`/use-cases/${p.slug}/`} className="card-hairline p-6 hover:border-brand-600 reveal-on-scroll">
              <div className="eyebrow">{p.meta?.deal}</div>
              <h3 className="mt-2 text-xl text-ink-950">{p.category}</h3>
              <p className="mt-2 text-sm text-ink-600">{p.meta?.workload}</p>
              <div className="mt-4 font-mono text-2xs text-ink-400">{(p.channels || []).join(" · ")}</div>
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}

export function UseCaseDetail() {
  const { slug } = useParams();
  const { data: p, isLoading } = useQuery({ queryKey: ["uc", slug], queryFn: () => get(`/content/use-case/${slug}`) });
  useReveal(p);
  if (isLoading) return <Spinner />;
  if (!p) return <Section><p className="text-ink-400">Not found.</p></Section>;
  return (
    <>
      <Seo title={p.title} path={`/use-cases/${slug}/`} description={p.description} jsonLd={[breadcrumbJsonLd([{name:"Use cases",path:"/use-cases/"},{name:p.category,path:`/use-cases/${slug}/`}]), articleJsonLd(p), faqJsonLd(p.faq)]} />
      <Section>
        <Breadcrumb items={[{name:"Use cases",path:"/use-cases/"},{name:p.category}]} />
        <h1 className="mt-6 text-4xl md:text-5xl tracking-tight text-ink-950 max-w-3xl">{p.title}</h1>
        <div className="mt-5 flex flex-wrap gap-2">
          <Badge>{p.meta?.deal}</Badge><Badge>{p.meta?.tone}</Badge><Badge>{p.meta?.cycle}</Badge>
        </div>
      </Section>
      <Section sunk>
        <div className="grid gap-10 lg:grid-cols-[1fr,320px]">
          <div><Markdown source={p.body} /></div>
          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="card-hairline p-5">
              <div className="eyebrow mb-2">A real conversation</div>
              <ConversationTranscript compact channel={(p.channels || [])[0] || "WhatsApp"} messages={[
                { from: "them", text: "interested but need to know if it fits my case", time: "10:01" },
                { from: "agent", text: "Happy to check. Two quick questions and I'll tell you straight, then book you in if it fits.", time: "10:01" },
              ]} />
            </div>
            <Button to="/pricing/" variant="primary" size="lg" className="w-full" data-testid="usecases-see-pricing-btn">See Pricing</Button>
          </aside>
        </div>
        {p.faq?.length > 0 && <div className="mt-12"><h2 className="text-xl text-ink-950 mb-4">FAQ</h2><FaqAccordion items={p.faq} /></div>}
        {p.related?.length > 0 && <div className="mt-12"><div className="eyebrow mb-4">Related use cases</div><div className="flex flex-wrap gap-3">{p.related.map((r) => <Link key={r.slug} to={`/use-cases/${r.slug}/`} className="link text-sm">{r.category}</Link>)}</div></div>}
      </Section>
    </>
  );
}
